import { toPageResult } from '@smartsash/server-ops';
import type { PaginationResultLike } from '@smartsash/server-ops';
import type { PageResult } from '@smartsash/types';
import type { QueryKey } from '@tanstack/react-query';

import type { DataScreenQueryOptions } from './types';

/** The part of a generated `queryOptions` object this helper needs. */
export interface PagedQueryOptions<TRaw> {
  queryKey: QueryKey;
  queryFn?: unknown;
  select?: (raw: TRaw) => unknown;
}

/**
 * Bridge a generated list `queryOptions` onto the `PageResult<TRow>` the factory reads (HS-869).
 *
 * The API answers `{items, totalCount, totalPages}` and the factory wants `{items, total, page,
 * pageSize}`. `toPageResult` already owns that mapping; without this wrapper every entity would
 * repeat the same `select` by hand:
 *
 * ```ts
 * query: (search) =>
 *   pagedQuery(getGetApiGroupQueryOptions({ page: search.page, pageSize: search.pageSize })),
 * ```
 *
 * It replaces any `select` the caller passed — map the rows inside `columns`/`mobileCard` instead,
 * so the cache keeps the DTO the rest of the app shares.
 */
export function pagedQuery<TRow>(
  options: PagedQueryOptions<PaginationResultLike<TRow>>,
): DataScreenQueryOptions<PageResult<TRow>> {
  return {
    ...options,
    select: toPageResult<TRow>,
  } as unknown as DataScreenQueryOptions<PageResult<TRow>>;
}
